import React from "react";
// UI Framework
import { toast } from "react-toastify";
// Common Components
import {
  TextInput,
  Button,
  DynamicDropdown,
  StaticDropdown,
  DynamicMultiSelectDropdown,
} from "src/Components";
// Hooks
import { useAppSelector } from "src/App/hooks";
// Request
import { getStepInstruments } from "src/Requests/stepRequests";
import { getBaseInstruments } from "src/Requests/Order/tradeTableRequests";
import {
  getPairType,
  getBaseType,
  getOffsetFilterSetting,
  setOffsetFilterSetting,
} from "src/Requests/synthetic";
// Types
import { IPairPositionSearchParams } from "src/Interfaces/Synthetic";
import { ExchangeType } from "src/Interfaces/connections";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
// Utilities
import * as R from "ramda";
import { packetErrorHandler } from "src/Utils/requestUtils";

const exchangeTypeOption: DropdownItem[] = [
  { id: ExchangeType.StockExchange, name: "اوراق بهادار" },
  { id: ExchangeType.CommodityExchange, name: "کالا" },
];

interface IProps {
  onClose: () => void;
  onApplied?: () => void;
}

const FilterSettingModal: React.FC<IProps> = ({ onClose, onApplied }) => {
  // store
  const riskProfiles = useAppSelector(
    (store) => store.coveredCall.riskProfiles,
  );
  // state
  const [loading, setLoading] = React.useState(false);
  const [riskProfileId] = React.useState<string>(
    riskProfiles[1]?.id?.toString() ?? "0",
  );
  const [searchParam, setSearchParam] =
    React.useState<IPairPositionSearchParams>({} as IPairPositionSearchParams);

  React.useEffect(() => {
    getOffsetFilterSetting()
      .then((resp) => {
        setSearchParam(resp);
      })
      .catch(packetErrorHandler);
  }, []);

  const searchParamChangeHandler = (name: string, value: any) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
  };

  const handleSearch = () => {
    setLoading(true);
    const cleanObject = R.pickBy(
      (v) => !(v === null || v === "" || (Array.isArray(v) && v.length === 0)),
      searchParam,
    );
    setOffsetFilterSetting(cleanObject as IPairPositionSearchParams)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          if (onApplied) onApplied();
          onClose();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <div className="border border-gray-300 rounded p-3 flex flex-col gap-3 !w-full !min-w-full">
      <div className="border border-gray-300 rounded p-3 flex flex-col gap-3 !w-full !min-w-full">
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <DynamicMultiSelectDropdown
              dropdown={{
                name: "baseInstrument",
                placeholder: "نماد پایه",
                extraLabel: true,
              }}
              getData={() => getBaseInstruments()}
              onChange={searchParamChangeHandler}
              value={searchParam?.baseInstrument}
            />
          </div>
          <div className="w-full">
            <StaticDropdown
              dropdown={{
                name: "exchangeType",
                placeholder: "بورس",
                extraLabel: true,
              }}
              onChange={searchParamChangeHandler}
              options={exchangeTypeOption}
              value={searchParam?.exchangeType?.toString() || ""}
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <DynamicMultiSelectDropdown
              dropdown={{
                name: "pairType",
                placeholder: "نوع جفت",
                extraLabel: true,
              }}
              getData={getPairType}
              onChange={searchParamChangeHandler}
              value={searchParam?.pairType}
            />
          </div>
          <div className="w-full">
            <DynamicDropdown
              dropdown={{
                name: "baseType",
                placeholder: "نوع پایه",
                extraLabel: true,
              }}
              getData={getBaseType}
              onChange={searchParamChangeHandler}
              value={searchParam?.baseType}
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <DynamicDropdown
              dropdown={{
                name: "buySideInstrument",
                placeholder: "نماد سمت خرید",
                extraLabel: true,
              }}
              getData={getStepInstruments}
              onChange={searchParamChangeHandler}
              value={searchParam?.buySideInstrument?.toString() || ""}
            />
          </div>
          <div className="w-full">
            <DynamicDropdown
              dropdown={{
                name: "sellSideInstrument",
                placeholder: "نماد سمت فروش",
                extraLabel: true,
              }}
              getData={getStepInstruments}
              onChange={searchParamChangeHandler}
              value={searchParam?.sellSideInstrument?.toString() || ""}
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              name="minYesterdayVolume"
              label="حداقل حجم معاملاتی روز قبل"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minYesterdayVolume?.toString() || ""}
              kind="percent"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="minYesterdayValue"
              label="حداقل ارزش معاملاتی رو ز قبل"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minYesterdayValue?.toString() || ""}
              kind="price"
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              name="minYearlyProfit"
              label="حداقل درصد بازده سالانه"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minYearlyProfit?.toString() || ""}
              kind="negativeDecimalNumbers"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="maxYearlyOffestProfit"
              label="حداکثر درصد بازده سالانه"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.maxYearlyOffestProfit?.toString() || ""}
              kind="negativeDecimalNumbers"
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              name="minCallITMPercentSide1"
              label="حداقل درصد ITM کال سمت خرید"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minCallITMPercentSide1?.toString() || ""}
              kind="negativeDecimalNumbers"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="minPutITMPercentSide1"
              label="حداقل درصد ITM پوت سمت خرید"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minPutITMPercentSide1?.toString() || ""}
              kind="negativeDecimalNumbers"
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-1/2">
            <TextInput
              name="minCallITMPercentSide2"
              label="حداقل درصد ITM کال سمت فروش"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minCallITMPercentSide2?.toString() || ""}
              kind="negativeDecimalNumbers"
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              name="minBuysideRemainingDay"
              label="حداقل DTM خرید"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.minBuysideRemainingDay?.toString() || ""}
              kind="number"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="maxBuysideRemainingDay"
              label="حداکثر DTM خرید"
              extraLabel
              onChange={searchParamChangeHandler}
              value={searchParam?.maxBuysideRemainingDay?.toString() || ""}
              kind="number"
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-start gap-3 w-full">
          <div className="w-full">
            <TextInput
              label="حداقل DTM فروش"
              onChange={searchParamChangeHandler}
              name="minSellsideRemainingDay"
              value={searchParam.minSellsideRemainingDay?.toString()}
              extraLabel
              kind="number"
            />
          </div>
          <div className="w-full">
            <TextInput
              name="maxSellsideRemainingDay"
              label="حداکثر DTM فروش"
              extraLabel
              kind="number"
              onChange={searchParamChangeHandler}
              value={searchParam?.maxSellsideRemainingDay?.toString()}
            />
          </div>
        </div>
      </div>
      <div className="w-64 mt-3 self-center">
        <Button
          onClick={handleSearch}
          text="ثبت"
          variant="contained"
          loading={loading}
        />
      </div>
    </div>
  );
};

export default FilterSettingModal;
